import React, { useState, useCallback, useEffect } from 'react';
import { Order } from '@hero/schema';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@hero/web-components';
import { OrderMap, OrderMapProps } from './OrderMap';
import { MapLocationData } from '@hero/web-components';

export interface OrderMapModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal open state changes */
  onOpenChange: (open: boolean) => void;
  /** The order to display */
  order: Order;
  /**
   * Callback when locations are saved - receives locations to be saved in
   * order DB. Should return Promise; modal closes only on success.
   *
   * Return `false` to keep the map open: the host has a follow-up step of
   * its own (a price decision, a blocking reason to read) and will close
   * the map itself once that resolves. Without this the map would vanish
   * behind the follow-up, leaving the user unable to see or amend the pins
   * they are being asked about.
   */
  onLocationSave?: (locations: MapLocationData[]) => void | boolean | Promise<void | boolean>;
  /**
   * Called when the pins settle (user clicked done on a stop) but before
   * they save — the hook for pre-save work such as quoting the new lane.
   * Pair with `savePreparing` to hold Save until it resolves.
   */
  onLocationsFinalized?: (locations: MapLocationData[]) => void;
  /** Whether the save request is in progress (e.g. from update order mutation isPending). Keeps modal open and shows loading on Save button. */
  saveInProgress?: boolean;
  /** Whether pre-save work is in flight; disables Save without claiming to be saving. */
  savePreparing?: boolean;
  /** What that pre-save work is, so Save can name it. */
  savePreparingReason?: OrderMapProps['savePreparingReason'];
  /** Optional orgId for saved locations */
  orgId?: OrderMapProps['orgId'];
  /** Optional customerId for saved locations */
  customerId?: OrderMapProps['customerId'];
  /** Optional Google Maps API key */
  apiKey?: string;
  /** Initial mode: 'view' | 'edit' | 'auto' (default: 'view') */
  initialMode?: 'view' | 'edit' | 'auto';
}

/**
 * OrderMapModal - Modal wrapper for OrderMap component
 * Manages modal state and handles location save callback
 */
export const OrderMapModal: React.FC<OrderMapModalProps> = ({
  open,
  onOpenChange,
  order,
  onLocationSave,
  onLocationsFinalized,
  saveInProgress = false,
  savePreparing = false,
  savePreparingReason,
  orgId,
  customerId,
  apiKey,
  initialMode = 'view',
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode === 'edit' ? 'edit' : 'view');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize mode when modal opens
  useEffect(() => {
    if (open) {
      setMode(initialMode === 'edit' ? 'edit' : 'view');
      setHasUnsavedChanges(false); // Reset on open
    }
  }, [open, initialMode]);

  // Handle locations when they're ready (finalized from map component). Stay open until save succeeds.
  const handleLocationsReady = useCallback(
    async (locations: MapLocationData[]) => {
      if (!onLocationSave) return;
      try {
        const outcome = await Promise.resolve(onLocationSave(locations));
        if (outcome === false) return;
        setHasUnsavedChanges(false);
        onOpenChange(false);
        setMode('view');
      } catch (error) {
        console.error('Error saving locations:', error);
        // Keep modal open so user can retry or close manually
      }
    },
    [onLocationSave, onOpenChange],
  );

  const handleLocationChange = useCallback((_location: MapLocationData, _index: number) => {
    setHasUnsavedChanges(true);
  }, []);

  const handleCancel = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  const handleClose = useCallback(() => {
    if (saveInProgress) return;
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setMode('view');
        setHasUnsavedChanges(false);
        onOpenChange(false);
      }
    } else {
      setMode('view');
      onOpenChange(false);
    }
  }, [hasUnsavedChanges, onOpenChange, saveInProgress]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-7xl w-[95vw] h-[90vh] p-0 gap-0 border-0 overflow-hidden"
        style={{ scrollbarGutter: 'auto' }}>
        <DialogHeader className="sr-only">
          <DialogTitle>Order Map</DialogTitle>
        </DialogHeader>
        <OrderMap
          order={order}
          mode={mode === 'edit' ? 'edit' : 'view'}
          readOnly={!onLocationSave}
          onLocationChange={handleLocationChange}
          onLocationsReady={handleLocationsReady}
          onLocationsFinalized={onLocationsFinalized}
          onCancel={handleCancel}
          onModeChange={setMode}
          saveInProgress={saveInProgress}
          savePreparing={savePreparing}
          savePreparingReason={savePreparingReason}
          className="h-full w-full overflow-hidden"
          apiKey={apiKey}
          orgId={orgId || order.shipper?._id?.toString()}
          customerId={customerId}
        />
      </DialogContent>
    </Dialog>
  );
};
